/**
 * CDN home for the curated order-device product photos. One folder per product
 * (`optistick`, `pro-player`, `promax-player`, `optikiosk-10|32|50`) where
 * `00.png` is the gallery hero and `01..NN` the rest of the gallery, matching
 * `deviceProducts.images` in Mongo.
 */
export const PRODUCT_IMAGE_BASE = `${import.meta.env.VITE_CDN_URL || 'https://cdn.optisigns.com'}/scio/order-device`;

/**
 * Fixed display order for order-device product UI (matched by product name,
 * case-insensitive): Stick Player → Pro Player → ProMax → Kiosk 10 → Kiosk 32
 * → Kiosk 50. Unknown products sort last.
 * `promax` is checked before `pro` so "ProMax" doesn't match the Pro Player rank.
 */
export function productOrderRank(name?: string): number {
  const n = (name ?? '').toLowerCase();
  if (n.includes('stick')) return 0;
  if (n.includes('promax') || n.includes('pro max')) return 2;
  if (n.includes('pro')) return 1;
  if (n.includes('10')) return 3;
  if (n.includes('32')) return 4;
  if (n.includes('50')) return 5;
  return 99;
}

/**
 * Local concept-stage display-name overrides, keyed by productOrderRank. The
 * Mongo `deviceProducts` names are verbose ("OptiSigns Android Stick Player
 * Pre-configured"); the order UI shows these shorter labels instead. Ranks
 * without an entry (the OptiKiosks) fall back to the trimmed Mongo name.
 * NOTE: concept-stage override — migrate into deviceProducts (or i18n) when
 * the naming is approved, same as OVERVIEW_BY_RANK / GALLERY_IMAGES_BY_RANK.
 */
const DISPLAY_NAME_BY_RANK: Record<number, string> = {
  0: 'OptiStick',
  1: 'Pro Player',
  2: 'ProMax Player',
};

/** Short display name for a product: local override when defined, else the
 *  trimmed Mongo name. Use everywhere the order-device UI shows a product name. */
export function productDisplayName(name?: string): string {
  return DISPLAY_NAME_BY_RANK[productOrderRank(name)] ?? (name ?? '').trim();
}
